// covered-by: frontends/carrie/src/ask-v2/shell/__tests__/AskV2Sidebar.test.tsx
import {
  ArrowLineLeft,
  ArrowLineRight,
  CaretRight,
  Gear,
  Plus,
  UserCircle,
  UserPlus,
} from "@phosphor-icons/react";
import {
  Box,
  DropdownMenu,
  Flex,
  IconButton,
  Link,
  Text,
  Tooltip,
} from "@radix-ui/themes";
import { Link as NavLink, useRouter } from "@tanstack/react-router";
import cx from "classnames";
import { usePostHog } from "posthog-js/react";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { AskV2SidebarRecents } from "./AskV2SidebarRecents";

import { AvatarWithInitials } from "../../components/AvatarWithInitials";
import { EditProfileDialog } from "../../components/EditProfileDialog";
import EnterpriseSwitcher from "../../components/EnterpriseSwitcher";
import { useAutoCollapseSidebar } from "../../components/hooks/useAutoCollapseSidebar";
import { ChatIcon } from "../../components/icons/ChatIcon";
import { KanbanIcon } from "../../components/icons/KanbanIcon";
import { InvitePeopleDialog } from "../../components/InvitePeopleDialog";
import { Sidebar } from "../../components/sidebar/Sidebar";
import { SidebarContent } from "../../components/sidebar/SidebarContent";
import { SidebarFooter } from "../../components/sidebar/SidebarFooter";
import { SidebarGroup } from "../../components/sidebar/SidebarGroup";
import { SidebarHeader } from "../../components/sidebar/SidebarHeader";
import { SidebarMenu } from "../../components/sidebar/SidebarMenu";
import { SidebarMenuButton } from "../../components/sidebar/SidebarMenuButton";
import { SidebarMenuItem } from "../../components/sidebar/SidebarMenuItem";
import { SidebarProvider } from "../../components/sidebar/SidebarProvider";
import { SidebarRail } from "../../components/sidebar/SidebarRail";
import { useSidebar } from "../../components/sidebar/useSidebar";
import { ReactComponent as Logo } from "../../images/Logo-blue.svg";
import { ReactComponent as Wordmark } from "../../images/Logotype.svg";
import { useAnalytics } from "../../utils/analytics";
import {
  canAccessAskPuntt,
  canAccessKnowledgeDatabase,
  canEnterpriseAdmin,
  canInvitePeople,
  canManageBrandGuidelines,
  canManageUsers,
  isMeaningfulGigsOrDemo,
  isMeaningfulGigsRole,
  isVitaloraDemoEnterprise,
} from "../../utils/auth";
import { isNil } from "../../utils/fp";
import { useAppDispatch, useAppSelector } from "../../utils/hooks";
import { logoutUrl } from "../../utils/logoutUrl";
import { resolveAvatarColors } from "../../utils/resolveAvatarColors";
import { logout } from "../../utils/slices/auth";
import { setDrawerOpen } from "../../utils/slices/ui";
import {
  ASK_V2_ARCHIVED_USERS_PATH,
  ASK_V2_CHATS_PATH,
  ASK_V2_ENTERPRISE_SETTINGS_PATH,
  ASK_V2_HOME_PATH,
  ASK_V2_INTEGRATION_SETTINGS_PATH,
  ASK_V2_KNOWLEDGE_DATABASE_PATH,
  ASK_V2_KNOWLEDGE_DATABASE_SEARCH,
  ASK_V2_KNOWLEDGE_SETTINGS_PATH,
  ASK_V2_OUTPUT_GUIDANCE_PATH,
  ASK_V2_NOTIFICATIONS_SETTINGS_PATH,
  ASK_V2_PROJECTS_PATH,
  ASK_V2_ROI_PATH,
  ASK_V2_SETTINGS_PATH,
  ASK_V2_USERS_PATH,
} from "../paths";

const navLinkClasses =
  "transition-colors [&_button]:transition-colors [&_button]:hover:bg-puntt-neutral-grayA-2";
const activeNavLinkClasses =
  "[&_button]:!bg-puntt-neutral-grayA-3 [&_button]:hover:!bg-puntt-neutral-grayA-4 [&_button]:active:!bg-puntt-neutral-grayA-5";
const SIDEBAR_NAV_ICON_BG = "rgb(var(--base-white))";

const SIDEBAR_FOOTER_BUTTON_CLASS =
  "group/menu-button !p-0 rounded-md transition-colors transform-none hover:bg-puntt-neutral-grayA-2 group-data-[state=open]/dropdown-menu:bg-puntt-neutral-grayA-3 group-data-[collapsible=icon]:w-full group-data-[collapsible=icon]:justify-center";
const SIDEBAR_FOOTER_GLYPH_CLASS =
  "flex size-6 shrink-0 items-center justify-center group-data-[collapsible=icon]:mx-auto";
const SIDEBAR_FOOTER_LABEL_CLASS =
  "px-1.5 py-1 group-data-[collapsible=icon]:hidden";

function SidebarTooltipLabel({
  icon,
  label,
}: {
  icon: ReactNode;
  label: string;
}) {
  return (
    <Flex align="center" className="gap-2">
      {icon}
      <Text
        size="1"
        className="puntt-link translate-y-1/2 opacity-0 duration-150 group-data-[state=delayed-open]/menu-item-tooltip:translate-y-0 group-data-[state=instant-open]/menu-item-tooltip:translate-y-0 group-data-[state=delayed-open]/menu-item-tooltip:opacity-100 group-data-[state=instant-open]/menu-item-tooltip:opacity-100"
      >
        {label}
      </Text>
    </Flex>
  );
}

function SidebarFooterMenu({
  open,
  onOpenChange,
  triggerTestId,
  ariaLabel,
  trigger,
  contentClassName,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  triggerTestId: string;
  ariaLabel: string;
  trigger: ReactNode;
  contentClassName?: string;
  children: ReactNode;
}) {
  const { open: railOpen } = useSidebar();
  const collapsed = !railOpen;
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = useCallback(() => {
    if (closeTimer.current != null) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  useEffect(() => cancelClose, [cancelClose]);

  const openNow = useCallback(() => {
    cancelClose();
    onOpenChange(true);
  }, [cancelClose, onOpenChange]);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => onOpenChange(false), 150);
  }, [cancelClose, onOpenChange]);

  const hoverTriggerProps = collapsed
    ? { onMouseEnter: openNow, onMouseLeave: scheduleClose }
    : {};
  const hoverContentProps = collapsed
    ? { onMouseEnter: cancelClose, onMouseLeave: scheduleClose }
    : {};

  return (
    <DropdownMenu.Root
      open={open}
      onOpenChange={onOpenChange}
      modal={!collapsed}
    >
      <DropdownMenu.Trigger
        className="group/dropdown-menu w-full"
        data-testid={triggerTestId}
        aria-label={ariaLabel}
        {...hoverTriggerProps}
      >
        {trigger}
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        side="right"
        className={contentClassName}
        {...hoverContentProps}
      >
        {children}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

function SidebarCollapseToggle() {
  const { open, toggleSidebar } = useSidebar();
  const { t } = useTranslation("common");
  const label = open ? t("nav.collapseSidebar") : t("nav.expandSidebar");

  return (
    <Tooltip content={label} side="right">
      <IconButton
        type="button"
        variant={open ? "ghost" : "surface"}
        color="gray"
        radius="full"
        size={open ? "2" : "1"}
        onClick={toggleSidebar}
        aria-label={label}
        data-testid="sidebar-collapse-trigger"
        className={cx(
          "shrink-0 text-puntt-neutral-gray-11",
          !open &&
            "absolute right-0 top-1/2 z-30 -translate-y-1/2 translate-x-1/2 bg-base-white shadow-sm !ring-1 ring-inset ring-puntt-neutral-gray-6 hover:!ring-2 hover:ring-puntt-neutral-gray-5",
        )}
      >
        {open ? <ArrowLineLeft size={16} /> : <ArrowLineRight size={16} />}
      </IconButton>
    </Tooltip>
  );
}

function AskV2SidebarHeader() {
  const { t } = useTranslation("common");
  const user = useAppSelector((state) => state.auth.value);
  const { open } = useSidebar();

  return (
    <SidebarHeader data-testid="ask-v2-sidebar-header" className="relative">
      {open ? (
        <Flex gap="3" align="center" width="100%">
          <NavLink
            to={ASK_V2_HOME_PATH}
            className="shrink-0"
            aria-label={t("nav.goToAskPuntt")}
            data-testid="sidebar-logo-link"
          >
            <Wordmark className="h-4 max-w-max" />
          </NavLink>
          {!isNil(user?.email) ? (
            <Flex align="center" className="min-w-0 flex-1">
              <EnterpriseSwitcher />
            </Flex>
          ) : (
            <div className="flex-1" />
          )}
          <SidebarCollapseToggle />
        </Flex>
      ) : (
        <>
          <NavLink
            to={ASK_V2_HOME_PATH}
            aria-label={t("nav.goToAskPuntt")}
            data-testid="sidebar-logo-link"
          >
            <Logo className="size-6" />
          </NavLink>
          <SidebarCollapseToggle />
        </>
      )}
    </SidebarHeader>
  );
}

function PrimaryNavItem({
  to,
  testId,
  label,
  tooltipLabel,
  renderIcon,
  exact,
}: {
  to: string;
  testId: string;
  label: string;
  tooltipLabel: string;
  renderIcon: () => ReactNode;
  exact?: boolean;
}) {
  return (
    <SidebarMenuItem data-testid={testId}>
      <Link size="3" weight="medium" className="puntt-link" asChild>
        <NavLink
          to={to}
          className={navLinkClasses}
          activeOptions={exact ? { exact: true } : undefined}
          activeProps={{ className: activeNavLinkClasses }}
        >
          <SidebarMenuButton
            tooltip={
              <Flex align="center" className="gap-3">
                {renderIcon()}
                <Text
                  size="3"
                  className="puntt-link translate-y-1/2 opacity-0 duration-150 group-data-[state=delayed-open]/menu-item-tooltip:translate-y-0 group-data-[state=instant-open]/menu-item-tooltip:translate-y-0 group-data-[state=delayed-open]/menu-item-tooltip:opacity-100 group-data-[state=instant-open]/menu-item-tooltip:opacity-100"
                >
                  {tooltipLabel}
                </Text>
              </Flex>
            }
          >
            <Box className="shrink-0 p-1">{renderIcon()}</Box>
            {label}
          </SidebarMenuButton>
        </NavLink>
      </Link>
    </SidebarMenuItem>
  );
}

export function AskV2Sidebar() {
  const posthog = usePostHog();
  const analytics = useAnalytics("AskV2Sidebar");
  const router = useRouter();
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const { t, i18n } = useTranslation("common");
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.value);
  const c = resolveAvatarColors(user ?? undefined);
  const sidebarOpen = useAppSelector((state) => state.ui.drawerOpen.app);

  // `auth.value` is ALSO populated for signed-out visitors: a share-link token
  // carries `enterpriseId`/`exp` but no `email` and no `role`. Several settings
  // predicates are not user-gated — `canAccessKnowledgeDatabase()` falls
  // through to a bare PostHog variant read (PostHog resolves flags for
  // anonymous distinct ids too) and `canAccessAskPuntt()` only consults the
  // cached enterprise brand-guidelines payload. So the gear could render for a
  // visitor who is simultaneously being shown "Log in or sign up", and every
  // item inside it dead-ends on an authenticated route. The footer already
  // treats `user.email` as THE signed-in signal for the account/login split;
  // reuse it here so the two halves of the footer cannot disagree.
  const isSignedIn = !isNil(user?.email);
  const canOpenSettings =
    isSignedIn &&
    (isMeaningfulGigsRole() ||
      isMeaningfulGigsOrDemo() ||
      canManageBrandGuidelines() ||
      canEnterpriseAdmin() ||
      canAccessKnowledgeDatabase() ||
      canManageUsers() ||
      canAccessAskPuntt() ||
      isVitaloraDemoEnterprise());

  useAutoCollapseSidebar();

  return (
    <SidebarProvider
      name="app"
      open={sidebarOpen}
      onOpenChange={(open) => {
        analytics.capture(`sidebar_${open ? "opened" : "closed"}`);
        dispatch(setDrawerOpen({ app: open }));
      }}
    >
      <Sidebar data-testid="ask-v2-sidebar">
        <AskV2SidebarHeader />

        <SidebarContent data-testid="ask-v2-sidebar-content">
          <Flex direction="column" className="min-h-0 flex-1 overflow-hidden">
            <SidebarGroup className="shrink-0 pb-0">
              <SidebarMenu>
                <PrimaryNavItem
                  to={ASK_V2_HOME_PATH}
                  testId="ask-v2-nav-home"
                  label={t("nav.new")}
                  tooltipLabel={t("nav.new")}
                  exact
                  renderIcon={() => (
                    <Plus
                      size={18}
                      color="rgb(var(--puntt-blue-12))"
                      aria-hidden="true"
                    />
                  )}
                />
                <PrimaryNavItem
                  to={ASK_V2_PROJECTS_PATH}
                  testId="ask-v2-nav-projects"
                  label={t("nav.projects")}
                  tooltipLabel={t("appSidebar.projects")}
                  renderIcon={() => (
                    <KanbanIcon
                      size={18}
                      bg={SIDEBAR_NAV_ICON_BG}
                      data-testid="ask-v2-nav-projects-icon"
                    />
                  )}
                />
                <PrimaryNavItem
                  to={ASK_V2_CHATS_PATH}
                  testId="ask-v2-nav-chats"
                  label={t("nav.chats")}
                  tooltipLabel={t("nav.chats")}
                  renderIcon={() => (
                    <ChatIcon
                      size={18}
                      bg={SIDEBAR_NAV_ICON_BG}
                      data-testid="ask-v2-nav-chats-icon"
                    />
                  )}
                />
              </SidebarMenu>
            </SidebarGroup>

            <AskV2SidebarRecents />
          </Flex>
        </SidebarContent>

        <SidebarFooter data-testid="ask-v2-sidebar-footer">
          <SidebarMenu>
            {canInvitePeople() && (
              <SidebarMenuItem>
                <SidebarMenuButton
                  type="button"
                  onClick={() => setIsInviteOpen(true)}
                  aria-label={t("nav.invitePeople")}
                  data-testid="sidebar-invite-people-trigger"
                  tooltipClassName="!-ml-[39.5px]"
                  tooltip={
                    <SidebarTooltipLabel
                      icon={
                        <UserPlus size={14} color="rgb(var(--puntt-blue-12))" />
                      }
                      label={t("nav.invitePeople")}
                    />
                  }
                  className={SIDEBAR_FOOTER_BUTTON_CLASS}
                >
                  <span
                    aria-hidden
                    data-testid="sidebar-invite-people-trigger-card"
                    className={SIDEBAR_FOOTER_GLYPH_CLASS}
                  >
                    <UserPlus
                      size={14}
                      className="text-puntt-neutral-gray-11"
                    />
                  </span>
                  <Flex width="100%" className={SIDEBAR_FOOTER_LABEL_CLASS}>
                    <Text
                      color="gray"
                      size="1"
                      weight="medium"
                      className="flex-1 text-left"
                    >
                      {t("nav.invitePeople")}
                    </Text>
                  </Flex>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}

            {canInvitePeople() && canOpenSettings && (
              <div
                role="separator"
                aria-orientation="horizontal"
                data-testid="account-menu-divider"
                className="-my-1 mx-3 h-px bg-puntt-neutral-gray-6 group-data-[collapsible=icon]:mx-auto group-data-[collapsible=icon]:w-6"
              />
            )}

            <SidebarMenuItem hidden={!canOpenSettings}>
              <SidebarFooterMenu
                open={isSettingsOpen}
                onOpenChange={setIsSettingsOpen}
                triggerTestId="sidebar-settings-trigger"
                ariaLabel={t("nav.settings")}
                trigger={
                  <SidebarMenuButton className={SIDEBAR_FOOTER_BUTTON_CLASS}>
                    <span
                      aria-hidden
                      data-testid="sidebar-settings-trigger-card"
                      className={SIDEBAR_FOOTER_GLYPH_CLASS}
                    >
                      <Gear size={14} className="text-puntt-neutral-gray-11" />
                    </span>
                    <Flex width="100%" className={SIDEBAR_FOOTER_LABEL_CLASS}>
                      <Text
                        color="gray"
                        size="1"
                        weight="medium"
                        className="flex-1 text-left"
                      >
                        {t("nav.settings")}
                      </Text>
                      <CaretRight />
                    </Flex>
                  </SidebarMenuButton>
                }
              >
                {isMeaningfulGigsRole() ? (
                  <DropdownMenu.Item data-testid="sidebar-settings-enterprise">
                    <NavLink
                      data-testid="ask-v2-sidebar-nav-link-enterprise-settings"
                      to={ASK_V2_ENTERPRISE_SETTINGS_PATH}
                      className="w-full"
                      onClick={() => setIsSettingsOpen(false)}
                    >
                      {t("appSidebar.enterprise")}
                    </NavLink>
                  </DropdownMenu.Item>
                ) : null}
                {isMeaningfulGigsOrDemo() ? (
                  <DropdownMenu.Item data-testid="sidebar-settings-roi">
                    <NavLink
                      data-testid="ask-v2-sidebar-nav-link-roi"
                      to={ASK_V2_ROI_PATH}
                      className="w-full"
                      onClick={() => setIsSettingsOpen(false)}
                    >
                      {t("nav.roiDashboard")}
                    </NavLink>
                  </DropdownMenu.Item>
                ) : null}
                {canManageBrandGuidelines() ? (
                  <DropdownMenu.Item data-testid="sidebar-settings-knowledge">
                    <NavLink
                      data-testid="ask-v2-sidebar-nav-link-knowledge-settings"
                      to={ASK_V2_KNOWLEDGE_SETTINGS_PATH}
                      className="w-full"
                      onClick={() => setIsSettingsOpen(false)}
                    >
                      {t("nav.knowledgeSettings")}
                    </NavLink>
                  </DropdownMenu.Item>
                ) : null}
                {canEnterpriseAdmin() ? (
                  <DropdownMenu.Item data-testid="sidebar-settings-output-guidance">
                    <NavLink
                      data-testid="ask-v2-sidebar-nav-link-output-guidance"
                      to={ASK_V2_OUTPUT_GUIDANCE_PATH}
                      className="w-full"
                      onClick={() => setIsSettingsOpen(false)}
                    >
                      {t("nav.outputGuidance")}
                    </NavLink>
                  </DropdownMenu.Item>
                ) : null}
                {canAccessKnowledgeDatabase() ? (
                  <DropdownMenu.Item data-testid="sidebar-settings-knowledge-database">
                    <NavLink
                      data-testid="ask-v2-sidebar-nav-link-knowledge-database"
                      to={ASK_V2_KNOWLEDGE_DATABASE_PATH}
                      search={ASK_V2_KNOWLEDGE_DATABASE_SEARCH}
                      className="w-full"
                      onClick={() => setIsSettingsOpen(false)}
                    >
                      {t("nav.knowledgeDatabase")}
                    </NavLink>
                  </DropdownMenu.Item>
                ) : null}
                {canManageUsers() ? (
                  <DropdownMenu.Item data-testid="sidebar-settings-integration">
                    <NavLink
                      data-testid="ask-v2-sidebar-nav-link-integration"
                      to={ASK_V2_INTEGRATION_SETTINGS_PATH}
                      className="w-full"
                      onClick={() => setIsSettingsOpen(false)}
                    >
                      {t("nav.integrationSettings")}
                    </NavLink>
                  </DropdownMenu.Item>
                ) : null}
                {canManageUsers() ? (
                  <DropdownMenu.Item data-testid="sidebar-settings-manage-users">
                    <NavLink
                      data-testid="ask-v2-sidebar-nav-link-users"
                      to={ASK_V2_USERS_PATH}
                      className="w-full"
                      onClick={() => setIsSettingsOpen(false)}
                    >
                      {t("nav.manageUsers")}
                    </NavLink>
                  </DropdownMenu.Item>
                ) : null}
                {canAccessAskPuntt() ? (
                  <DropdownMenu.Item data-testid="sidebar-settings-notifications">
                    <NavLink
                      data-testid="ask-v2-sidebar-nav-link-notifications"
                      to={ASK_V2_NOTIFICATIONS_SETTINGS_PATH}
                      className="w-full"
                      onClick={() => setIsSettingsOpen(false)}
                    >
                      {t("nav.notifications")}
                    </NavLink>
                  </DropdownMenu.Item>
                ) : null}
                {canManageUsers() ? (
                  <DropdownMenu.Item>
                    <NavLink
                      data-testid="ask-v2-sidebar-nav-link-archived-users"
                      to={ASK_V2_ARCHIVED_USERS_PATH}
                      className="w-full"
                      onClick={() => setIsSettingsOpen(false)}
                    >
                      {t("nav.archivedUsers")}
                    </NavLink>
                  </DropdownMenu.Item>
                ) : null}
                {isVitaloraDemoEnterprise() ? (
                  <DropdownMenu.Item data-testid="sidebar-settings-demo">
                    <NavLink
                      data-testid="ask-v2-sidebar-nav-link-demo-settings"
                      to={ASK_V2_SETTINGS_PATH}
                      hash="demo-settings"
                      className="w-full"
                      onClick={() => setIsSettingsOpen(false)}
                    >
                      {t("nav.demoSettings")}
                    </NavLink>
                  </DropdownMenu.Item>
                ) : null}
              </SidebarFooterMenu>
            </SidebarMenuItem>

            <SidebarMenuItem hidden={isSignedIn}>
              {/* why: V2 has no AuthenticationDialog overlay — navigate to /login instead of a disabled no-op */}
              <SidebarMenuButton
                onClick={() => {
                  location.assign(logoutUrl(router.basepath));
                }}
                data-testid="sidebar-login-trigger-card"
                aria-label={t("nav.logInOrSignUp")}
                tooltipClassName="!-ml-[39.5px]"
                tooltip={
                  <SidebarTooltipLabel
                    icon={
                      <UserCircle size={14} color="rgb(var(--puntt-blue-12))" />
                    }
                    label={t("nav.logInOrSignUp")}
                  />
                }
                className={SIDEBAR_FOOTER_BUTTON_CLASS}
              >
                <span aria-hidden className={SIDEBAR_FOOTER_GLYPH_CLASS}>
                  <UserCircle
                    size={14}
                    className="text-puntt-neutral-gray-11"
                  />
                </span>
                <Flex width="100%" className={SIDEBAR_FOOTER_LABEL_CLASS}>
                  <Text
                    color="gray"
                    size="1"
                    weight="medium"
                    className="flex-1 whitespace-nowrap text-left"
                  >
                    {t("nav.logInOrSignUp")}
                  </Text>
                </Flex>
              </SidebarMenuButton>
            </SidebarMenuItem>

            <SidebarMenuItem hidden={!isSignedIn}>
              <SidebarFooterMenu
                open={isAccountOpen}
                onOpenChange={setIsAccountOpen}
                triggerTestId="sidebar-account-trigger"
                ariaLabel={t("nav.account")}
                contentClassName="min-w-[220px]"
                trigger={
                  <SidebarMenuButton className={SIDEBAR_FOOTER_BUTTON_CLASS}>
                    <AvatarWithInitials
                      size={6}
                      name={user?.name ?? ""}
                      avatar={user?.avatar}
                      colorKey={c.colorKey}
                      backgroundColor={c.backgroundColor}
                      textColor={c.textColor}
                      data-testid="sidebar-account-trigger-card"
                      className="size-6 shrink-0 group-data-[collapsible=icon]:mx-auto"
                    />
                    <Flex width="100%" className={SIDEBAR_FOOTER_LABEL_CLASS}>
                      <Text
                        color="gray"
                        size="1"
                        weight="medium"
                        className="flex-1 text-left"
                      >
                        {t("nav.account")}
                      </Text>
                      <CaretRight />
                    </Flex>
                  </SidebarMenuButton>
                }
              >
                <div className="flex flex-col items-center py-4">
                  <AvatarWithInitials
                    size={16}
                    name={user?.name ?? ""}
                    avatar={user?.avatar}
                    colorKey={c.colorKey}
                    backgroundColor={c.backgroundColor}
                    textColor={c.textColor}
                  />
                  <span className="mt-2 text-center text-lg font-semibold">
                    {user?.name}
                  </span>
                  <span className="text-gray-500 mt-1 text-center text-sm">
                    {user?.email}
                  </span>
                </div>
                <div className="border-gray-200 my-2 border-t" />
                <DropdownMenu.Item
                  onClick={() => {
                    setIsEditProfileOpen(true);
                    setIsAccountOpen(false);
                  }}
                  className="cursor-pointer"
                  data-testid="sidebar-account-edit-profile"
                >
                  {t("nav.editProfile")}
                </DropdownMenu.Item>
                <div className="border-gray-200 my-2 border-t" />
                <DropdownMenu.Sub>
                  <DropdownMenu.SubTrigger
                    className="cursor-pointer"
                    data-testid="account-language-menu"
                  >
                    {t("language.label")}
                  </DropdownMenu.SubTrigger>
                  <DropdownMenu.SubContent>
                    {(
                      [
                        { code: "en", name: "English" },
                        { code: "es", name: "Español" },
                        { code: "pt-BR", name: "Português (Brasil)" },
                        { code: "fr", name: "Français" },
                        { code: "de", name: "Deutsch" },
                        { code: "ar", name: "العربية" },
                      ] as const
                    ).map(({ code, name }) => (
                      <DropdownMenu.Item
                        key={code}
                        onClick={() => {
                          void i18n.changeLanguage(code);
                          setIsAccountOpen(false);
                          void import("../../services/userProfile").then(
                            ({ updateMinimalUserProfile }) =>
                              updateMinimalUserProfile({ uiLanguage: code }),
                          );
                        }}
                        className="cursor-pointer"
                        data-testid={`account-language-${code}`}
                      >
                        {name}
                      </DropdownMenu.Item>
                    ))}
                  </DropdownMenu.SubContent>
                </DropdownMenu.Sub>
                <div className="border-gray-200 my-2 border-t" />
                <DropdownMenu.Item
                  onClick={() => {
                    dispatch(logout());
                    posthog.reset();
                    setIsAccountOpen(false);
                  }}
                  className="text-red-600 cursor-pointer"
                  data-testid="sidebar-account-logout"
                >
                  {t("nav.logOut")}
                </DropdownMenu.Item>
              </SidebarFooterMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
        <SidebarRail data-testid="ask-v2-sidebar-rail" />
      </Sidebar>

      <EditProfileDialog
        open={isEditProfileOpen}
        onOpenChange={setIsEditProfileOpen}
      />
      <InvitePeopleDialog open={isInviteOpen} onOpenChange={setIsInviteOpen} />
    </SidebarProvider>
  );
}
